import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, Pencil, X, Send } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", slug: "", excerpt: "", body: "", status: "draft" };

const useBootstrap = (fn) => {
  useEffect(() => {
    fn();
  }, [fn]);
};

const AdminNewsletter = () => {
  const { admin, logout, getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);

  let saveLabel = editing === "new" ? "Create issue" : "Save changes";
  if (saving) saveLabel = "Saving…";

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${getToken()}` } }), [getToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/newsletter`, auth());
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useBootstrap(load);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      title: it.title || "",
      slug: it.slug || "",
      excerpt: it.excerpt || "",
      body: it.body || "",
      status: it.status || "draft",
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        body: form.body,
        status: form.status,
      };
      if (form.slug.trim()) payload.slug = form.slug.trim();

      if (editing === "new") {
        await axios.post(`${API}/admin/newsletter`, payload, auth());
        toast.success("Issue created.");
      } else {
        await axios.patch(`${API}/admin/newsletter/${editing}`, payload, auth());
        toast.success("Issue updated.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (it) => {
    try {
      await axios.patch(
        `${API}/admin/newsletter/${it.id}`,
        { status: it.status === "published" ? "draft" : "published" },
        auth()
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const sendIssue = async (it, force = false) => {
    if (!force && !window.confirm(`Email "${it.title}" to all subscribers?`)) return;
    setSending(it.id);
    try {
      const { data } = await axios.post(`${API}/admin/newsletter/${it.id}/send`, { force }, auth());
      toast.success(
        `Sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}.` +
          (data.failed ? ` ${data.failed} failed — check backend logs.` : "")
      );
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm("This issue was already sent. Send again to all subscribers?")) {
          return sendIssue(it, true);
        }
      } else {
        toast.error(formatApiError(e?.response?.data?.detail));
      }
    } finally {
      setSending(null);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/${it.id}`, auth());
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <main data-testid="admin-newsletter-page" className="min-h-screen bg-[#F7F7F4]">
      <SEO title="Newsletter" path="/admin/newsletter" noindex />
      <header className="sticky top-0 z-30 bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/admin" className="font-sora text-[22px] font-extrabold tracking-tight text-[#111111]">
            Vaymilo<span className="text-[#2563EB]">.</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-manrope text-sm text-[#111111]/65">{admin?.email}</span>
            <button
              onClick={logout}
              data-testid="btn-admin-logout"
              className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope text-sm px-4 py-2 rounded-lg border border-[#E7E7E2] hover:border-[#111111]"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="overline">— Newsletter</div>
        <h1 className="mt-3 font-sora text-[40px] md:text-[52px] font-extrabold tracking-tight text-[#111111]">Issues.</h1>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <Link to="/admin" className="font-manrope text-sm text-[#111111]/65 hover:text-[#111111]">← Back to dashboard</Link>
          {editing === null && (
            <button
              onClick={openNew}
              data-testid="newsletter-new-btn"
              className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-sm px-4 py-2.5 rounded-lg"
            >
              <Plus size={14} /> New issue
            </button>
          )}
        </div>

        {/* Editor */}
        {editing !== null && (
          <form
            onSubmit={save}
            data-testid="newsletter-editor"
            className="mt-10 bg-white border border-[#E7E7E2] rounded-2xl p-6 md:p-8 space-y-5"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-sora text-[22px] font-bold tracking-tight text-[#111111]">
                {editing === "new" ? "New issue" : "Edit issue"}
              </h2>
              <button type="button" onClick={cancel} className="text-[#111111]/55 hover:text-[#111111]">
                <X size={18} />
              </button>
            </div>

            <div>
              <label className="block font-manrope text-sm mb-2">Title</label>
              <input
                required
                data-testid="nl-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Issue #1 — How we ship calm software"
                className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]"
              />
            </div>

            <div>
              <label className="block font-manrope text-sm mb-2">
                Slug <span className="text-[#111111]/45">(optional — auto-derived from title)</span>
              </label>
              <input
                data-testid="nl-slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="how-we-ship-calm-software"
                className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB] font-mono"
              />
            </div>

            <div>
              <label className="block font-manrope text-sm mb-2">Excerpt</label>
              <textarea
                required
                data-testid="nl-excerpt"
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                rows={2}
                placeholder="One or two sentences. Shown on the archive list."
                className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]"
              />
            </div>

            <div>
              <label className="block font-manrope text-sm mb-2">Body</label>
              <textarea
                required
                data-testid="nl-body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={14}
                placeholder="Plain text. Use blank lines to separate paragraphs. Line breaks are preserved."
                className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#2563EB] font-manrope leading-relaxed"
              />
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 font-manrope text-sm">
                <input
                  type="checkbox"
                  data-testid="nl-publish-toggle"
                  checked={form.status === "published"}
                  onChange={(e) => setForm({ ...form, status: e.target.checked ? "published" : "draft" })}
                />
                Publish (visible on /newsletter)
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={cancel}
                  className="bg-white text-[#111111] font-manrope text-sm px-4 py-2.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="nl-save"
                  className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-manrope font-medium text-sm px-5 py-2.5 rounded-lg"
                >
                  {saveLabel}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Table */}
        <section className="mt-12 bg-white border border-[#E7E7E2] rounded-2xl overflow-hidden">
          <table data-testid="newsletter-table" className="w-full font-manrope text-[14px]">
            <thead className="bg-[#F7F7F4]">
              <tr>
                {["Title", "Slug", "Status", ""].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55 border-b border-[#E7E7E2]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-[#111111]/50 font-mono text-xs uppercase tracking-widest">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-[#111111]/60">
                    No issues yet. Create your first one.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((it) => (
                  <tr key={it.id} className="border-b border-[#E7E7E2] last:border-b-0">
                    <td className="px-5 py-4">
                      <div className="font-sora text-[15px] font-semibold text-[#111111]">{it.title}</div>
                      <div className="text-[12px] text-[#111111]/55 mt-0.5 line-clamp-1">{it.excerpt}</div>
                    </td>
                    <td className="px-5 py-4 font-mono text-[12px] text-[#111111]/70">{it.slug}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => togglePublish(it)}
                        data-testid={`nl-toggle-${it.slug}`}
                        className={`px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider ${
                          it.status === "published" ? "bg-[#2563EB] text-white" : "bg-[#F7F7F4] text-[#111111]/55"
                        }`}
                      >
                        {it.status === "published" ? "Live" : "Draft"}
                      </button>
                      {it.sent_at && (
                        <span
                          data-testid={`nl-sent-badge-${it.slug}`}
                          className="ml-2 px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider bg-[#EAF1FE] text-[#2563EB]"
                          title={`Emailed to ${it.sent_count ?? 0} subscribers`}
                        >
                          Sent · {it.sent_count ?? 0}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        {it.status === "published" && (
                          <button
                            onClick={() => sendIssue(it)}
                            disabled={sending === it.id}
                            data-testid={`nl-send-${it.slug}`}
                            className="text-[#111111]/55 hover:text-[#2563EB] disabled:opacity-40"
                            title="Email to all subscribers"
                          >
                            <Send size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(it)}
                          data-testid={`nl-edit-${it.slug}`}
                          className="text-[#111111]/55 hover:text-[#2563EB]"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => remove(it)}
                          data-testid={`nl-delete-${it.slug}`}
                          className="text-[#111111]/45 hover:text-[#2563EB]"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      </div>
    </main>
  );
};

export default AdminNewsletter;
